import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authService } from "../../services/authService";
import { toast } from "react-toastify";
import "./login.css";

const VerifyOTP = () => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(600); // 10 minutes in seconds
  const [resendCooldown, setResendCooldown] = useState(0); // Cooldown for resend (30 seconds)
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);

  const identifier = location.state?.identifier;
  const method = location.state?.method || "phone";
  const userType = location.state?.user_type;
  const email = location.state?.email;
  const maskedPhone = location.state?.masked_phone;

  useEffect(() => {
    if (!identifier) {
      toast.error("Invalid request. Please start from forget password page.");
      navigate("/forget-password");
      return;
    }

    // Start countdown timer for OTP expiration
    const timerInterval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Start countdown timer for resend cooldown
    const resendInterval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timerInterval);
      clearInterval(resendInterval);
    };
  }, [identifier, navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...otp];
    newOtp[index] = value.replace(/[^0-9]/g, "");
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    const newOtp = pastedData.split("").slice(0, 6);
    while (newOtp.length < 6) {
      newOtp.push("");
    }
    setOtp(newOtp);
    inputRefs.current[Math.min(newOtp.length - 1, 5)]?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      toast.error("Please enter complete 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      // Verify OTP by attempting to reset password (we'll get the OTP verification in reset step)
      // Actually, we need to verify OTP first, then navigate to reset password
      // For now, let's navigate to reset password with OTP
      navigate("/reset-password", {
        state: {
          identifier,
          method,
          user_type: userType,
          otp: otpString,
          email: email,
        },
      });
    } catch (error) {
      console.error("Verify OTP error:", error);
      toast.error(error.response?.data?.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    // Check if resend is on cooldown
    if (resendCooldown > 0) {
      toast.info(`Please wait ${resendCooldown} seconds before resending OTP`);
      return;
    }

    setLoading(true);
    try {
      // If email is available, use email to resend (will send to phone)
      const data = email ? { email: email } : { phone: identifier };
      const response = await authService.forgetPassword(data);

      if (response.success) {
        toast.success(response.message || "OTP resent successfully");
        setTimer(600); // Reset OTP expiration timer
        setResendCooldown(30); // Set 30 second cooldown
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();

        // Update masked phone if provided in response
        if (response.data?.masked_phone) {
          // You can update the state if needed
        }
      } else {
        toast.error(response.message || "Failed to resend OTP");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      toast.error(error.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        {/* Left Illustration */}
        <div className="login-left">
          <img src={process.env.PUBLIC_URL + "/login_page.jpg"} alt="Illustration" />
        </div>

        {/* Right Form */}
        <div className="login-right">
          <div className="logo-container">
            <img src={process.env.PUBLIC_URL + "/pickloo.jpeg"} alt="Pickloo Logo" className="logo" />
          </div>

          <h2>Verify OTP</h2>
          <p>Enter the 6-digit code sent to your phone number</p>
          {maskedPhone && (
            <p
              style={{
                textAlign: "center",
                color: "#666",
                fontSize: "14px",
                marginTop: "-10px",
                marginBottom: "10px",
              }}
            >
              {maskedPhone}
            </p>
          )}

          <form onSubmit={handleVerify} onPaste={handlePaste}>
            <div className="form-group" style={{ marginBottom: "30px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  marginBottom: "20px",
                }}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    style={{
                      width: "50px",
                      height: "50px",
                      textAlign: "center",
                      fontSize: "24px",
                      fontWeight: "bold",
                      borderRadius: "8px",
                      border: "2px solid #ddd",
                    }}
                    required
                  />
                ))}
              </div>

              {timer > 0 && (
                <p
                  style={{
                    textAlign: "center",
                    color: "#666",
                    fontSize: "14px",
                    marginBottom: "10px",
                  }}
                >
                  OTP expires in: {formatTime(timer)}
                </p>
              )}

              <div style={{ textAlign: "center", marginTop: "10px" }}>
                {resendCooldown > 0 ? (
                  <p
                    style={{
                      color: "#999",
                      fontSize: "14px",
                      margin: "0",
                    }}
                  >
                    Resend OTP in {resendCooldown}s
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#333",
                      cursor: loading ? "not-allowed" : "pointer",
                      textDecoration: "underline",
                      fontSize: "14px",
                      opacity: loading ? 0.6 : 1,
                    }}
                  >
                    {loading ? "Sending..." : "Resend OTP"}
                  </button>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading || otp.join("").length !== 6}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>

            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <button
                type="button"
                onClick={() => navigate("/forget-password")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#333",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontSize: "14px",
                }}
              >
                Change Phone Number
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;
